import { useMutation } from "@apollo/client";
import cx from "classnames";

import FlashContext from "components/FlashMessages/FlashContext";
import PolicyTypeBadge from "components/PolicyTypeBadge";
import { Spinner } from "components/Spinner";
import { Trash } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import CardCollapsible from "ds/components/Card/Collapsible";
import { CardCollapsibleAriaLevel } from "ds/components/Card/Collapsible/components/types";
import Tag from "ds/components/Tag";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import { Maybe, PolicyStackAttachment } from "types/generated";

import { AttachedPolicy, AutoAttachedPolicy, ManuallyAttachablePolicyType } from "../types";
import NewModuleAttachedPolicyContent from "./Content";
import { DETACH_POLICY } from "./gql";
import styles from "./styles.module.css";

type NewModuleAttachedPolicyProps = {
  item: AttachedPolicy | AutoAttachedPolicy;
  isSummaryView?: boolean;
  headingLevel: CardCollapsibleAriaLevel;
};

const NewModuleAttachedPolicy = ({
  item,
  isSummaryView,
  headingLevel,
}: NewModuleAttachedPolicyProps) => {
  const { name, attachmentId, body, type } = item;
  const isAutoAttached = "autoAttachableLabel" in item;
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const [detachPolicy, { loading }] = useMutation<{ policyDetach: Maybe<PolicyStackAttachment> }>(
    DETACH_POLICY,
    {
      refetchQueries: ["GetAttachedModulePolicies"],
      awaitRefetchQueries: true,
    }
  );

  const handleDetach = () => {
    detachPolicy({ variables: { attachmentId } })
      .then(({ data }) => {
        if (data?.policyDetach?.id) {
          reportSuccess({ message: "Policy was detached" });
        }
      })
      .catch(onError);
  };

  const canDetach = !!attachmentId && !isSummaryView;

  const [isCollapsed, toggle] = useToggle(true);

  return (
    <CardCollapsible
      isCollapsed={isCollapsed}
      onToggle={toggle}
      ariaLevel={headingLevel}
      ariaLabel={name}
      title={name}
      titleVariant="p-t7"
      headerSuffix={
        <>
          <Box
            align="center"
            gap="large"
            justify="end"
            grow="1"
            shrink={isAutoAttached ? undefined : "0"}
            className={cx(isAutoAttached && styles.badgeAutoattachContainer)}
          >
            {isAutoAttached && (
              <div className={styles.badgeWrapper}>
                <Tag tag={item.autoAttachableLabel} withAutoAttach />
              </div>
            )}

            {!isAutoAttached && <PolicyTypeBadge type={type as ManuallyAttachablePolicyType} />}
          </Box>
          {canDetach && (
            <ButtonIcon
              disabled={loading}
              icon={loading ? Spinner : Trash}
              onPress={handleDetach}
              variant={loading ? "ghost" : "ghostDanger"}
            >
              Detach
            </ButtonIcon>
          )}
        </>
      }
    >
      <NewModuleAttachedPolicyContent policyBody={body} />
    </CardCollapsible>
  );
};

export default NewModuleAttachedPolicy;
